// Mocks for the Datastore integration tests
import { HttpClient } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import * as Rx from 'rxjs';
import {
  Backend,
  BackendModule,
  CollectionActions,
  DatastoreCollection,
  DatastoreDocument,
  mergeEntities,
  OrderByDirection,
  Reference,
  RequestStatus,
  StoreBackend,
  StoreFeatureState,
  SuccessResponseData,
  transformObjects,
} from '..';
import { BackendFetchTypes } from '../../backend.model';
import { Collections } from '../../collections.model';

// MockObject is a fake model that can be extended with mock fields in order to
// support the Datastore tests
export interface MockObject {
  readonly id: number;
  readonly username: string;
  readonly displayName?: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly rating: number;
}

export function getMockObject(
  id: string | number,
  name: string = 'mockObject',
  jobs: ReadonlyArray<string> = ['js', 'html', 'css'],
  rating: number = 10,
): MockObject {
  return {
    id: Number(id),
    username: name,
    displayName: name,
    jobs,
    rating,
  };
}

// Mock response from API must have `status` and `result` fields.
// The request is originally made in freelancer-http.service, which is the
// location where Angular's native HTTP methods are called.
export interface MockObjectGetResultApi {
  readonly mockObjects: { readonly [k in string]?: MockObject };
}

export function getMockObjectResponse({
  ids,
  name = 'mockObject',
  jobs = ['js', 'html', 'css'],
  rating = 10,
}: {
  readonly ids: ReadonlyArray<string | number>;
  readonly name?: string;
  readonly jobs?: ReadonlyArray<string>;
  readonly rating?: number;
}): SuccessResponseData<BackendFetchTypes['testing']> {
  return {
    result: {
      mockObjects: ids.reduce(
        (acc, id) => ({
          ...acc,
          [id]: getMockObject(id, name, jobs, rating),
        }),
        {},
      ),
    },
    status: 'success',
  };
}

// DatastoreTestingModule is the mock collection used in the Datastore
// integration tests
function mockTransform(backendObj: any): any {
  return backendObj;
}

export function mockReducer(
  state: StoreFeatureState<'testing'> = {},
  action: CollectionActions<'testing'>,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'testing') {
        const { result, ref, order } = action.payload;
        if (result.mockObjects) {
          return mergeEntities<'testing'>(
            state,
            transformObjects(result.mockObjects, mockTransform as any),
            order,
            ref,
          );
        }
        return state;
      }

      return state;
    }

    case 'WS_MESSAGE': {
      return state;
    }

    default:
      return state;
  }
}

export function mockBackend(): Backend<'testing'> {
  return {
    defaultOrder: { field: 'id', direction: OrderByDirection.ASC },
    fetch: (_authUid, _userIds, _query) => ({
      endpoint: 'testing/0.1/testing',
    }),
  };
}

export class DatastoreCollectionMock<
  K extends keyof Collections
> extends DatastoreCollection<K> {
  constructor(valueChanges$: Rx.Observable<ReadonlyArray<Collections[K]>>) {
    super(
      Rx.of({} as Reference<K>),
      {} as StoreBackend,
      Rx.of({} as RequestStatus),
      valueChanges$,
    );
  }
}

export class DatastoreDocumentMock<
  T extends keyof Collections
> extends DatastoreDocument<T> {
  constructor(valueChanges$: Rx.Observable<Collections[T]>) {
    super(
      Rx.of({} as Reference<T> & { readonly path: { readonly ids: [string] } }),
      {} as StoreBackend,
      Rx.of({} as RequestStatus),
      valueChanges$,
    );
  }
}

@NgModule({
  imports: [
    StoreModule.forFeature('testing', mockReducer),
    BackendModule.forFeature('testing', mockBackend),
  ],
})
export class DatastoreTestingModule {}

// api-http.service mock
@Injectable()
export class MockApiHttp {
  constructor(private http: HttpClient) {}

  fetch() {
    return this.http.get('');
  }
}
